// GROUND-M0 GROUND HEIGHT + NODE CHOICE (pure; client and server share it).
//
// groundAt: the ground mesh's height under world (x, z) on ONE decoded node —
// a SINGLE surface, the ground only: the triangle holding the point (walls,
// zero plan area, never; nor the cut's walls / seal). The mesh is single-valued: a point lies in one
// triangle except on an edge, where the triangles agree — but on a kerb line,
// where the road copy and the walk top share the plan edge: the higher (the
// walk top, borecut.js skinAt's rule). A bore opening is a hole: NaN, and the
// caller's multi-level resolver (game/spans.js) decides, as it does today.
//
// selectNodes: the nodes drawn for one eye — a quadtree from the roots down,
// a node refined while its screen error err * K / d > px (d = the eye's
// distance to the node's box) and all four children exist; then 2:1
// balanced (no drawn node next to one two levels finer), so a crack between
// neighbours is at most err(self) + err(one level coarser).

// a triangle labelled NOSTAND (tin.js: the cut's walls, seal) is
// drawn, never stood on — borecut.js skinAt's rule
const NOSTAND = 16;
const NODE_M0 = 128;        // an L0 node's side (m); level k: 128 * 2^k
export const nodeSide = (k) => NODE_M0 * (1 << k);

// the level cell (cx, cy) of side cs lies wholly outside the frame [0, 0,
// world.w, world.h] — any side, cx / cy may be negative (the far tree's
// nodes W / N of the frame); morph.js prepIndex and selectNodes share it
export function offFrame(cx, cy, cs, world) {
  return cx * cs >= world.w || cy * cs >= world.h || (cx + 1) * cs <= 0 || (cy + 1) * cs <= 0;
}

// a per-node bucket grid over the triangles (built once per decoded node)
export function indexNode(node, cells = 64) {
  const side = nodeSide(node.level) + 0.5 * (1 << node.level);
  const cs = side / cells, B = new Array(cells * cells);
  const { x, z, idx, lab } = node;
  for (let t = 0; t < idx.length / 3; t++) {
    if (lab && lab[t] & NOSTAND) continue;                     // walls, seal
    const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
    const ax = x[a], az = z[a], bx = x[b] - ax, bz = z[b] - az, cx = x[c] - ax, cz = z[c] - az;
    if (!(Math.abs(bx * cz - bz * cx) > 1e-12)) continue;      // a wall
    const i0 = Math.max(0, Math.floor(Math.min(x[a], x[b], x[c]) / cs)), i1 = Math.min(cells - 1, Math.floor(Math.max(x[a], x[b], x[c]) / cs));
    const j0 = Math.max(0, Math.floor(Math.min(z[a], z[b], z[c]) / cs)), j1 = Math.min(cells - 1, Math.floor(Math.max(z[a], z[b], z[c]) / cs));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) (B[j * cells + i] || (B[j * cells + i] = [])).push(t);
  }
  node.grid = { cells, cs, B };
  return node;
}

// node-local (lx, lz) -> y or NaN (outside the node / a hole); hit (optional)
// gets .t = the triangle that gave y (-1 none)
export function groundAt(node, lx, lz, hit = null) {
  const g = node.grid || indexNode(node).grid;
  const i = Math.floor(lx / g.cs), j = Math.floor(lz / g.cs);
  if (i < 0 || j < 0 || i >= g.cells || j >= g.cells) return NaN;
  const L = g.B[j * g.cells + i];
  if (hit) hit.t = -1;
  if (!L) return NaN;
  const { x, z, y, idx } = node;
  let best = NaN;
  for (const t of L) {
    const a = idx[3 * t], b = idx[3 * t + 1], c = idx[3 * t + 2];
    const ax = x[a], az = z[a], bx = x[b] - ax, bz = z[b] - az, cx = x[c] - ax, cz = z[c] - az;
    const den = bx * cz - bz * cx;
    const px = lx - ax, pz = lz - az, l1 = (px * cz - pz * cx) / den, l2 = (bx * pz - bz * px) / den;
    if (l1 < -1e-9 || l2 < -1e-9 || l1 + l2 > 1 + 1e-9) continue;
    const v = y[a] + l1 * (y[b] - y[a]) + l2 * (y[c] - y[a]);
    if (!(v <= best)) { best = v; if (hit) hit.t = t; }
  }
  return best;
}

// index = { nodes: Map key 'k_tx_ty' -> { k, tx, ty, err, y0, y1 }, world:
// { w, h, outer } m (a child missing wholly off the frame: not needed — with
// outer (the far tree) its quadrant is the node's own, morph.js prepIndex's
// rule: as a quad entry, else the node cannot refine) }, top = the root
// level; eye {x, y, z}; K px / rad; px the target; balance: 2:1 (the
// skirt-era crack bound; a stitched mesh needs none); stand: R_STAND (m) —
// a node whose plan box comes within it of the eye is refined down to L0
// whatever its error (never coarse ground under the player). -> keys drawn.
export function selectNodes(index, top, eye, K, px = 2, balance = true, stand = 0, quads = false) {
  const out = new Map();     // key -> node
  const box = (n) => {
    const s = nodeSide(n.k), o = 0.25, x0 = n.tx * s + o, z0 = n.ty * s + o;
    const dx = Math.max(x0 - eye.x, 0, eye.x - (x0 + s)), dz = Math.max(z0 - eye.z, 0, eye.z - (z0 + s));
    const dy = Math.max(n.y0 - eye.y, 0, eye.y - n.y1);
    return Math.hypot(dx, dy, dz);
  };
  const W = index.world, outer = !!(W && W.outer);
  // the children present ({ ...node, held: q }: the node's own quadrant q)
  const kids = (n) => {
    if (n.k === 0) return null;
    const r = [];
    let any = false;
    for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
      const cx = 2 * n.tx + dx, cy = 2 * n.ty + dy, cs = nodeSide(n.k - 1);
      const c = index.nodes.get(`${n.k - 1}_${cx}_${cy}`);
      if (!c) {
        if (W && offFrame(cx, cy, cs, W)) {
          if (!outer) continue;
          if (!quads) return null;
          r.push({ ...n, held: dy * 2 + dx });
          continue;
        }
        return null;
      }
      r.push(c); any = true;
    }
    return any || !outer ? r : null;
  };
  const plan = (n) => {
    const s = nodeSide(n.k), o = 0.25, x0 = n.tx * s + o, z0 = n.ty * s + o;
    return Math.hypot(Math.max(x0 - eye.x, 0, eye.x - (x0 + s)), Math.max(z0 - eye.z, 0, eye.z - (z0 + s)));
  };
  const want = (n) => n.err * K / Math.max(box(n), 1e-3) > px || (stand > 0 && plan(n) < stand);
  // quads: a refined node keeps the quadrants its own error still serves
  // (judged over the child's box) and refines only the others — key
  // 'k_tx_ty#q' (q = dy * 2 + dx), the node's leaves in that quadrant
  // (codec: every RTIN leaf below level 2 lies in one quadrant)
  const walk = (n) => {
    const c = want(n) ? kids(n) : null;
    if (!c) { out.set(`${n.k}_${n.tx}_${n.ty}`, n); return; }
    for (const ch of c) {
      if (ch.held !== undefined) { out.set(`${n.k}_${n.tx}_${n.ty}#${ch.held}`, { ...n, quad: ch.held }); continue; }
      if (quads && n.err * K / Math.max(box({ ...ch, err: n.err }), 1e-3) <= px && !(stand > 0 && plan(ch) < stand)) {
        const q = (ch.ty - 2 * n.ty) * 2 + (ch.tx - 2 * n.tx);
        out.set(`${n.k}_${n.tx}_${n.ty}#${q}`, { ...n, quad: q });
      } else walk(ch);
    }
  };
  for (const n of index.nodes.values()) if (n.k === top) walk(n);
  // 2:1 balance: split a drawn node with a drawn neighbour two levels finer
  for (let changed = balance; changed;) {
    changed = false;
    for (const [key, n] of out) {
      if (n.k < 2) continue;
      const s = nodeSide(n.k), x0 = n.tx * s, z0 = n.ty * s;
      let finer = false;
      for (const m of out.values()) {
        if (m.k > n.k - 2) continue;
        const t = nodeSide(m.k), mx = m.tx * t, mz = m.ty * t;
        const touch = mx <= x0 + s && mx + t >= x0 && mz <= z0 + s && mz + t >= z0;
        if (touch) { finer = true; break; }
      }
      const c = finer ? kids(n) : null;
      if (c) {
        out.delete(key);
        for (const ch of c) {
          if (ch.held !== undefined) out.set(`${n.k}_${n.tx}_${n.ty}#${ch.held}`, { ...n, quad: ch.held });
          else out.set(`${ch.k}_${ch.tx}_${ch.ty}`, ch);
        }
        changed = true;
      }
    }
  }
  return out;
}
